import api from '../api';
import { emptyJoints } from '../types';
import type { JointAngles, JointKey } from '../types';
import { buildBidirectionalMappings } from './jointMapping';

export type SimulationCsvRow = {
  frame: number;
  values: number[];
};

export type SimulationCsvParseResult = {
  headers: string[];
  rows: SimulationCsvRow[];
  matrix: number[][];
};

export type SimulationJsonRow = Record<string, string | null | undefined>;

export type SimulationJsonResponse = SimulationJsonRow[];

/** 浮动基座变换（root link 的全局位姿） */
export type RootTransform = {
  /** 位置 (x, y, z)，单位：米 */
  pos: [number, number, number];
  /** 方向四元数 (x, y, z, w) */
  quat: [number, number, number, number];
};

export type LoadSimulationDataResult = {
  rawData: SimulationJsonResponse;
  frames: JointAngles[];
  /** 每帧的浮动基座变换（对应 CSV 中 root_pos_* / root_rot_* 字段） */
  rootTransforms: RootTransform[];
  /** JointKey → CSV 中的 URDF 关节名（动态构建，用于 3D 渲染驱动 URDF 关节） */
  jointKeyToUrdfName: Partial<Record<JointKey, string>>;
  /** CSV 字段名列表（含 root 列），用于计算关节列索引 */
  csvFieldNames: string[];
};

// ===== 工具函数 =====

function radiansToDegrees(value: number): number {
  return (value * 180) / Math.PI;
}

function degreesToRadians(value: number): number {
  return (value * Math.PI) / 180;
}

function toSafeNumber(value: string | null | undefined): number {
  if (value == null) return 0;
  const normalized = String(value).trim();
  if (!normalized || normalized.toLowerCase() === 'none') return 0;
  const num = Number(normalized);
  return Number.isFinite(num) ? num : 0;
}

// ===== 帧数据构建 =====

/**
 * 从 CSV 行数据中提取浮动基座变换。
 * root_pos_x/y/z → 位移（米），root_rot_x/y/z/w → 四元数。
 */
function extractRootTransform(row: SimulationJsonRow): RootTransform {
  const pos: [number, number, number] = [
    toSafeNumber(row['root_pos_x']),
    toSafeNumber(row['root_pos_y']),
    toSafeNumber(row['root_pos_z']),
  ];
  const quat: [number, number, number, number] = [
    toSafeNumber(row['root_rot_x']),
    toSafeNumber(row['root_rot_y']),
    toSafeNumber(row['root_rot_z']),
    toSafeNumber(row['root_rot_w']),
  ];
  return { pos, quat };
}

/** 空的根变换（原点，无旋转） */
const EMPTY_ROOT: RootTransform = { pos: [0, 0, 0], quat: [0, 0, 0, 1] };

/**
 * 从 API 响应的一行数据构建 JointAngles。
 * API 返回的字段名是 URDF 关节名，值为弧度。
 * 内部统一使用角度（degrees）存储。
 *
 * @param row - API 原始行数据
 * @param urdfToJointKey - 动态构建的 CSV字段 → JointKey 映射
 */
function buildSimulationFrameFromRecord(
  row: SimulationJsonRow,
  urdfToJointKey: Record<string, JointKey>,
): JointAngles {
  const joints = emptyJoints();

  for (const fieldName of Object.keys(row)) {
    if (fieldName.startsWith('root_')) continue;

    const jointKey = urdfToJointKey[fieldName];
    if (!jointKey) continue;

    const rawValue = toSafeNumber(row[fieldName]);
    joints[jointKey] = radiansToDegrees(rawValue);
  }

  return joints;
}

/**
 * 将编辑后的帧数据写回 API 格式（URDF 关节名 → 弧度字符串）。
 * 未修改的字段保留原始数据中的值。
 *
 * @param originalData - 原始 API 响应数据
 * @param frames - 编辑后的帧数据
 * @param jointKeyToUrdf - 动态构建的 JointKey → CSV字段 映射
 */
export function buildSimulationPayloadFromFrames(
  originalData: SimulationJsonResponse,
  frames: JointAngles[],
  jointKeyToUrdf: Partial<Record<JointKey, string>>,
  rootTransforms?: RootTransform[],
): SimulationJsonResponse {
  const payload = structuredClone(originalData);

  payload.forEach((row, index) => {
    const frame = frames[index];
    if (!frame) return;

    for (const jointKey of Object.keys(jointKeyToUrdf) as JointKey[]) {
      const urdfName = jointKeyToUrdf[jointKey];
      if (!urdfName) continue;

      const value = frame[jointKey];
      if (value == null) continue;

      row[urdfName] = String(degreesToRadians(value));
    }
  });

  // 写入浮动基座变换（如果提供了 rootTransforms）
  if (rootTransforms && rootTransforms.length > 0) {
    payload.forEach((row, index) => {
      const rt = rootTransforms[index];
      if (!rt) return;
      row['root_pos_x'] = String(rt.pos[0]);
      row['root_pos_y'] = String(rt.pos[1]);
      row['root_pos_z'] = String(rt.pos[2]);
      row['root_rot_x'] = String(rt.quat[0]);
      row['root_rot_y'] = String(rt.quat[1]);
      row['root_rot_z'] = String(rt.quat[2]);
      row['root_rot_w'] = String(rt.quat[3]);
    });
  }

  return payload;
}

// ===== 数据加载 =====

/**
 * 从 API 加载仿真 JSON 数据，返回解析后的帧数组。
 * API 响应格式：{ data: [{ URDF关节名: 弧度值字符串, ... }, ...] }
 *
 * CSV 字段名即为 URDF 关节名。此函数会动态匹配 CSV 字段名到标准 JointKey，
 * 并返回反向映射 jointKeyToUrdfName 供 3D 渲染时直接驱动 URDF 关节。
 *
 * @param simulationId - 仿真任务 ID
 * @param userId - 用户 ID
 * @param signal - 可选 AbortSignal，用于取消请求
 * @param source - 数据来源: "current"(当前最新) | "backup"(初始备份)，默认 "current"
 */
export async function loadSimulationCsv(
  simulationId: string,
  userId: string,
  signal?: AbortSignal,
  source: 'current' | 'backup' = 'current',
): Promise<LoadSimulationDataResult> {
  const rawData = (await api.simulation.downloadCsv(simulationId, source, {
    headers: { 'x-user-id': userId },
    timeout: 60000,
    signal,
  })) as SimulationJsonResponse;

  if (rawData.length === 0) {
    throw new Error('Invalid simulation response: empty CSV data');
  }

  // 从首行数据中提取所有 CSV 字段名，动态构建关节映射
  const firstRow = rawData[0];
  const csvFieldNames = firstRow ? Object.keys(firstRow) : [];
  const { urdfToJointKey, jointKeyToUrdf } = buildBidirectionalMappings(csvFieldNames);

  // 日志：列出匹配到的关节和未匹配的字段
  const matchedFields = Object.keys(urdfToJointKey);
  const unmatchedFields = csvFieldNames.filter(
    (f) => !f.startsWith('root_') && f && !urdfToJointKey[f],
  );
  console.log(
    `[loadSimulationCsv] 动态关节映射: ${matchedFields.length} 个关节已匹配` +
      (unmatchedFields.length > 0 ? `, ${unmatchedFields.length} 个字段未匹配: [${unmatchedFields.join(', ')}]` : ''),
  );

  // 将每一行 JSON 数据转换为 JointAngles 帧和根变换
  const frames: JointAngles[] = [];
  const rootTransforms: RootTransform[] = [];
  for (const row of rawData) {
    frames.push(buildSimulationFrameFromRecord(row, urdfToJointKey));
    rootTransforms.push(extractRootTransform(row));
  }

  console.log(
    `[loadSimulationCsv] 已解析 ${frames.length} 帧，` +
    `首帧采样关节: ${JSON.stringify(frames[0]).slice(0, 120)}...，` +
    `根位姿: pos=[${rootTransforms[0].pos.map(v => v.toFixed(3)).join(',')}] quat=[${rootTransforms[0].quat.map(v => v.toFixed(3)).join(',')}]`,
  );

  return { rawData, frames, rootTransforms, jointKeyToUrdfName: jointKeyToUrdf, csvFieldNames };
}

/**
 * 解析原始 CSV 文本到 JSON 行数组。
 * 自动检测表头行（如果首行包含非数字 token）。
 *
 * @param csvText - CSV 文件内容
 * @param fieldNames - 字段名列表（对应 CSV 的列顺序）
 * @returns 解析后的 JSON 格式数据
 */
function parseRawCsvToJsonRows(
  csvText: string,
  fieldNames: string[],
): SimulationJsonResponse {
  const lines = csvText
    .trim()
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0);

  const dataLines = (() => {
    if (lines.length === 0) return lines;
    const first = lines[0];
    const delimiter = first.includes('\t') && !first.includes(',') ? '\t' : ',';
    const firstTokens = first.split(delimiter).map((v) => v.trim()).filter(Boolean);
    const looksLikeHeader = firstTokens.some((token) => {
      const n = Number(token);
      return !Number.isFinite(n);
    });
    return looksLikeHeader ? lines.slice(1) : lines;
  })();

  const rows: SimulationJsonResponse = [];

  for (const line of dataLines) {
    const delimiter = line.includes('\t') && !line.includes(',') ? '\t' : ',';
    const values = line.split(delimiter).map((v) => v.trim());
    const row: SimulationJsonRow = {};
    for (let i = 0; i < fieldNames.length; i++) {
      row[fieldNames[i]] = values[i] ?? '0';
    }
    rows.push(row);
  }

  return rows;
}

/**
 * 从本地 CSV 文件内容或 JSON 数据加载仿真数据。
 * 适用于无表头的纯数值 CSV，或直接传入 JSON 数组。
 */
export function loadLocalSimulationCsv(
  csvTextOrData: string | SimulationJsonResponse,
  fieldNames: string[],
): LoadSimulationDataResult {
  const rawData = typeof csvTextOrData === 'string'
    ? parseRawCsvToJsonRows(csvTextOrData, fieldNames)
    : csvTextOrData;

  if (rawData.length === 0) {
    throw new Error('CSV 文件为空或解析失败');
  }

  const { urdfToJointKey, jointKeyToUrdf } = buildBidirectionalMappings(fieldNames);

  const matchedFields = Object.keys(urdfToJointKey);
  const unmatchedFields = fieldNames.filter(
    (f) => !f.startsWith('root_') && f && !urdfToJointKey[f],
  );
  console.log(
    `[loadLocalSimulationCsv] 动态关节映射: ${matchedFields.length} 个关节已匹配` +
      (unmatchedFields.length > 0 ? `, ${unmatchedFields.length} 个字段未匹配: [${unmatchedFields.join(', ')}]` : ''),
  );

  const frames: JointAngles[] = [];
  const rootTransforms: RootTransform[] = [];
  for (const row of rawData) {
    frames.push(buildSimulationFrameFromRecord(row, urdfToJointKey));
    rootTransforms.push(extractRootTransform(row));
  }

  return { rawData, frames, rootTransforms, jointKeyToUrdfName: jointKeyToUrdf, csvFieldNames: fieldNames };
}
